import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isAfter, isBefore, subYears } from 'date-fns';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Card, Col, Row } from 'react-bootstrap';
import CustomerProfileForm from 'components/profile/CustomerProfileForm';
import PrimaryButton from 'shared/components/PrimaryButton';
import LoadingSpinner from 'shared/components/spinner/LoadingSpinner';
import accountService from 'accessors/accountService';
import ToastUtility from 'utilities/ToastUtility';
import InputValidators from 'utilities/InputValidators';
import CreateAccount from 'models/account/create';

type Mode = 'signin' | 'createaccount';

interface AccountGateTabProps {
  postLoginPath?: string;
}

const AccountGateTab: React.FC<AccountGateTabProps> = ({ postLoginPath = '/loan/create' }) => {
  const { loginWithRedirect } = useAuth0();
  const [mode, setMode] = useState<Mode>('signin');
  const [form, setForm] = useState<CreateAccount>({});
  const [isLoading, setIsLoading] = useState(false);
  const [validPhone, setValidPhone] = useState(false);
  const [validEmail, setValidEmail] = useState(false);
  const [termsOfUseAgree, setTermsOfUseAgree] = useState(false);

  const currentYear = new Date().getFullYear();

  const validateData = (): boolean => {
    if (!termsOfUseAgree) return false;

    const validFirstName = form.firstName != undefined && form.firstName.length > 0;
    const validLastName = form.lastName != undefined && form.lastName.length > 0;

    const validDOB = form.dateOfBirth !== undefined
      && form.dateOfBirth.toString().length > 0
      && !isBefore(new Date(form.dateOfBirth), subYears(new Date(), 125))
      && !isAfter(new Date(form.dateOfBirth), new Date());

    let validBusinessName = true;
    if (form.businessName != undefined) validBusinessName = form.businessName.length > 0;

    const validAddress = form.streetAddress !== undefined && form.streetAddress.length > 0;
    const validCity = form.city !== undefined && form.city.length > 0;
    const validState = form.state !== undefined && form.state !== null && form.state.length > 0;
    const validZipCode = InputValidators.IsValidZipCode(form.zipCode ?? '');
    const validPhoneField = validPhone && form.phoneNumber != undefined && form.phoneNumber.length > 0;
    const validEmailField = validEmail && form.emailAddress != undefined && form.emailAddress.length > 0;

    const validPassword = form.password != undefined && form.confirmPassword != undefined
      && InputValidators.IsValidPassword(form.password)
      && form.password.length > 0 && form.confirmPassword.length > 0
      && form.password === form.confirmPassword;

    const validFarmingStart = form.farmingStartYear !== undefined
      && form.farmingStartYear > 0
      && +form.farmingStartYear >= (currentYear - 100)
      && +form.farmingStartYear <= currentYear;

    return validFirstName && validLastName && validDOB && validBusinessName
      && validAddress && validCity && validState && validZipCode
      && validPhoneField && validEmailField && validPassword && validFarmingStart;
  };

  const handleSignIn = () => {
    loginWithRedirect({ appState: { returnTo: postLoginPath } });
  };

  const handleCreateAccount = async () => {
    if (!validateData()) return;

    setIsLoading(true);
    try {
      const accountExists = await accountService.CheckIfAccountExists(form.emailAddress ?? '');

      if (accountExists) {
        ToastUtility.showErrorToast('An account with that email already exists. Please sign in or reset your password.');
        setIsLoading(false);
        return;
      }

      const success = await accountService.CreateAccount(form);

      if (success) {
        loginWithRedirect({ appState: { returnTo: postLoginPath } });
      } else {
        ToastUtility.showErrorToast('Error creating account. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      ToastUtility.showErrorToast('Error creating account. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100">
      {isLoading && <LoadingSpinner variant="dark" animation="border" />}
      <Row className="mt-4">
        <Col xs={12}>
          <Card className="p-4">
            <h3 className="mb-3 text-center">Sign In to Continue</h3>
            <p className="text-center text-muted">Please sign in or create an account to start your loan application.</p>
            <div className="d-flex justify-content-center mb-3">
              <PrimaryButton buttonText="Sign In" onClick={handleSignIn} />
            </div>
            <hr />
            {mode === 'signin' ? (
              <div className="text-center">
                <span>New to agGRO?&nbsp;</span>
                <button className="btn btn-link p-0" onClick={() => setMode('createaccount')}>Create an account</button>
              </div>
            ) : (
              <>
                <h4 className="mb-3">Create Account</h4>
                <CustomerProfileForm
                  mode="create"
                  email={form.emailAddress}
                  firstName={form.firstName}
                  lastName={form.lastName}
                  suffix={form.suffix}
                  dateOfBirth={form.dateOfBirth}
                  businessName={form.businessName}
                  streetAddress={form.streetAddress}
                  streetAddress2={form.streetAddress2}
                  city={form.city}
                  state={form.state}
                  zipCode={form.zipCode}
                  phoneNumber={form.phoneNumber}
                  farmingStartYear={form.farmingStartYear}
                  password={form.password}
                  confirmPassword={form.confirmPassword}
                  termsOfUseAgree={termsOfUseAgree}
                  onEmailChange={(e) => {
                    const emailAddress = e.target.value;
                    setValidEmail(InputValidators.IsValidEmailAddress(emailAddress));
                    setForm(prev => ({ ...prev, emailAddress }));
                  }}
                  onFirstNameChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                  onLastNameChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                  onSuffixChange={(e) => setForm(prev => ({ ...prev, suffix: e.target.value }))}
                  onDOBChange={(date) => setForm(prev => ({ ...prev, dateOfBirth: date }))}
                  onBusinessNameChange={(e) => setForm(prev => ({ ...prev, businessName: e.target.value }))}
                  onStreetAddressChange={(e) => setForm(prev => ({ ...prev, streetAddress: e.target.value }))}
                  onStreetAddress2Change={(e) => setForm(prev => ({ ...prev, streetAddress2: e.target.value }))}
                  onCityChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                  onStateChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))}
                  onZipCodeChange={(e) => setForm(prev => ({ ...prev, zipCode: e.target.value }))}
                  onPhoneChange={(value, _data, _event, formattedValue) => {
                    setValidPhone(isValidPhoneNumber(`+1 ${formattedValue}`));
                    setForm(prev => ({ ...prev, phoneNumber: value }));
                  }}
                  onFarmingStartYearChange={(e) => setForm(prev => ({ ...prev, farmingStartYear: +e.target.value }))}
                  onPasswordChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  onConfirmPasswordChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  onTermsAgreeChange={(checked) => setTermsOfUseAgree(checked)}
                />
                <div className="mt-3 d-flex justify-content-between">
                  <button className="btn btn-link p-0" onClick={() => setMode('signin')}>Back to Sign In</button>
                  <PrimaryButton
                    buttonText="Create Account"
                    onClick={handleCreateAccount}
                    disabled={!validateData()}
                  />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountGateTab;
